import {
  useGetDistributionSummaryByIdQuery,
  useListCalculationStepsQuery,
  useReadDistributionProcessingQuery,
} from "@/services/distribution/distributionReducers";
import { getStatusDescription, havingEditPermission } from "@/utility/common";
import {
  MotifButton,
  MotifProgressLoader,
  MotifToast,
} from "@ey-xd/motif-react";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DistributionEditForm from "../distributionsSetup/distributionEdit/DistributionEditForm";
import { useTranslation } from "react-i18next";
import { dateFunc } from "@/components/Functions/dateFunc";
import { setDistributionStatus } from "@/pages/CaseDetail/Distributions/DistributionsSlice";
import { useDispatch } from "react-redux";
import GenerateDraftCalculation from "./GenerateDraftCalculation";
import { useCase } from "@/features/case/use-case";
import { useSelector } from "react-redux";
import DistributionContext from "./DistributionContext";
import { EDIT_BUTTON_VARIANT } from "@/components/Images/CommonImages";
import { paginationPageSize } from "@/Constant";
import CopyInputBox from "@/components/Form/InputBox/CopyInputBox";
import { usePermission } from "@/features/case/use-permission";
const DistributionSummaryTab = () => {
  const { distributionId, caseId } = useParams();
  const { data, isLoading, refetch } = useGetDistributionSummaryByIdQuery({
    id: distributionId,
  });
  const { data: processingData } = useReadDistributionProcessingQuery(
    distributionId,
    {
      skip: !distributionId,
    }
  );
  const distributionContextData: any = useContext<any>(DistributionContext);
  const closeMessageHandler = () => {
    setNotification({ ...notification, show: false });
  };
  const [shouldFetchCasePermissions, setShouldFetchCasePermissions] =
    useState(false);
  usePermission(caseId, shouldFetchCasePermissions);
  const [paginationObj] = useState({
    distributionId: distributionId,
    PageNumber: 1,
    PageSize: paginationPageSize,
    SearchText: "",
    SortOrder: "asc",
    SortColumn: "SortOrder",
  });
  const { data: setpsData, isLoading: stepsDataLoading } =
    useListCalculationStepsQuery(paginationObj);

  const dispatch = useDispatch();
  const caseData = useCase();
  const permissions = useSelector((state: any) => state.permissions);
  const { Distribution } = permissions;
  const { t } = useTranslation();

  const renderMessage = () => {
    if (notification.show) {
      return (
        <MotifToast
          onClose={closeMessageHandler}
          //@ts-ignore
          variant={notification.type}
          supportingText={
            [1, 2].includes(processingData?.processingStatus) &&
            notification.type === "info"
              ? t("global.globalNotifications.progressingMessage", {
                  status: processingData?.processingStatusMessage,
                })
              : ""
          }
        >
          {notification.type === "error" ? (
            (
              <>
                <div>{t(notification.message)}</div>
              </>
            ) || <div>{t("global.globalNotifications.unExpectedError")}</div>
          ) : notification.type === "success" ? (
            (
              <>
                <div>{t(notification.message)}</div>
              </>
            ) || (
              <div>{t("global.globalNotifications.updatedSuccessfully")}</div>
            )
          ) : (
            <>
              <div>{t(notification.message)}</div>
            </>
          )}
        </MotifToast>
      );
    }
  };
  const caseDetails = [
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.distributionName"
      ),
      value: data?.name,
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.distributionNameFrench"
      ),
      value: data?.nameFrench,
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.description"
      ),
      value: data?.description,
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.descriptionFrench"
      ),
      value: data?.descriptionFrench,
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.createdFromPreviousDistribution"
      ),
      value: data?.previousDistributionName || "",
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.status"
      ),
      value: getStatusDescription(data?.distributionStatus),
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.distributionDate"
      ),
      value: dateFunc(data?.distributionDate).formattedDateOnly,
    },

    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.effectiveDate"
      ),
      value: dateFunc(data?.effectiveDate).formattedDateOnly,
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.createdOn"
      ),
      value:
        dateFunc(data?.createdOn).formattedDateTime +
        " " +
        caseData?.abbreviation,
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.entitlementCalculation"
      ),
      value: dateFunc(data?.lastEntitlementRunDate).formattedDateOnly,
    },
    {
      title: t(
        "pages.distributions.distribution.distributionFormLabels.lastSettlementCalculation"
      ),
      value: dateFunc(data?.lastSettlementRunDate).formattedDateOnly,
    },
  ];

  const [notification, setNotification] = useState({
    show: false,
    message: "",
    supportingMessage: "",
    type: "success",
  });

  useEffect(() => {
    refetch();
  }, [refetch]);
  useEffect(() => {
    if (data?.distributionStatus !== undefined) {
      dispatch(setDistributionStatus(data.distributionStatus));
    }
    //eslint-disable-next-line
  }, [notification, data]);

  if (distributionContextData.editDistribution) {
    //@ts-ignore
    return (
      <DistributionEditForm
        distributionData={data}
        notification={notification}
        setNotification={setNotification}
        refetch={refetch}
        setShouldFetchCasePermissions={setShouldFetchCasePermissions}
      />
    );
  }
  interface CaseDetail {
    title: string;
    value: string;
  }

  const numberOfColumns = 4;
  const chunkArray = (array: CaseDetail[], chunkSize: number) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const caseDetailsFields = chunkArray(caseDetails, numberOfColumns);

  function handleSummaryApiAfterSettlementDone() {
    refetch();
  }
  return (
    <div>
      {isLoading ? (
        <MotifProgressLoader show variant="default" className="mar-b-20" />
      ) : (
        <>
          {renderMessage()}
          <div className="motif-container case-summary-form pad-v-20 mar-t-20">
            <div className="motif-row justify-content-between align-items-center">
              <div className="motif-col-lg-11">
                <div className="motif-body1-default-regular page-title">
                  {t(
                    "pages.distributions.distribution.distributionViewPage.title"
                  )}
                </div>
                <div className="motif-body2-default-regular page-description">
                  {t(
                    "pages.distributions.distribution.distributionViewPage.description"
                  )}
                </div>
              </div>
              <div className="motif-col-lg-1">
                {havingEditPermission(Distribution) && (
                  <MotifButton
                    size="small"
                    type="submit"
                    variant={EDIT_BUTTON_VARIANT}
                    onClick={() => {
                      setShouldFetchCasePermissions(false);
                      distributionContextData.setEditDistribution(true);
                    }}
                    disabled={
                      data?.distributionStatus === 3 ||
                      [1, 2]?.includes(processingData?.processingStatus) ||
                      processingData?.processingStatus === undefined
                    }
                  >
                    {t("global.globalActions.edit")}
                  </MotifButton>
                )}
              </div>
            </div>
          </div>
          <div className="motif-container main-container pad-v-60 mar-b-80">
            {caseDetailsFields.map((caseDetailsField, rowIndex) => (
              <div
                key={rowIndex}
                className={`motif-row ${rowIndex > 0 ? "pad-t-40" : ""}`}
              >
                {caseDetailsField.map((detail, colIndex) => (
                  <div key={colIndex} className="motif-col-lg-3">
                    <div>
                      <div className="title">{detail.title}</div>
                      <div className="motif-body2-default-bold">
                        {detail.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div className="pad-t-40 ">
              <CopyInputBox
                value={data?.executionLog}
                isReadonly={true}
                label={t(
                  "pages.distributions.distribution.distributionFormLabels.executionLog"
                )}
              />
            </div>
          </div>

          {
            <GenerateDraftCalculation
              handleSummaryApiAfterSettlementDone={
                handleSummaryApiAfterSettlementDone
              }
              setpsData={setpsData?.results}
              distributionName={distributionContextData.distributionName}
              isPending={stepsDataLoading}
              processingData={processingData}
              setNotification={setNotification}
            />
          }
        </>
      )}
    </div>
  );
};

export default DistributionSummaryTab;
